import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import cytoscape, { Core, CytoscapeOptions, EventObject, LayoutOptions, NodeSingular } from 'cytoscape';
import { ApiService } from 'src/app/shared';
import {
  GatewayDetailExtras,
  GatewayResolutionGraph,
  GraphNodeFlags,
  GraphNodeType,
  buildGraphPath,
  nextMaxItems,
  normalizeDefaultPaths,
  normalizeGraph,
  normalizeSeedKeys,
  resolveStart,
  toGatewayExtras,
} from './gateway-resolution-graph.model';
import {
  NODE_ICON_CLASS,
  RING_OF,
  RenderedGuide,
  RingType,
  computeRingGeometry,
  neighbourSet,
  ringLayoutOptions,
  toElements,
  toRenderedGuides,
} from './gateway-resolution-graph.elements';

/** Colours/shapes per node type. The legend swatches in the .less use the same colours. */
const GRAPH_STYLE: any[] = [
  {
    selector: 'node',
    style: {
      width: 'data(size)',
      height: 'data(size)',
      label: 'data(glyph)',
      'font-family': 'primeicons',
      'font-size': 'data(glyphSize)',
      color: '#ffffff',
      'text-valign': 'center',
      'text-halign': 'center',
      'min-zoomed-font-size': 6,
      'border-width': 1,
      'border-color': '#ffffff',
    },
  },
  { selector: 'node.gateway', style: { shape: 'diamond', 'background-color': '#7c3aed' } },
  { selector: 'node.paymentType', style: { shape: 'round-rectangle', 'background-color': '#0284c7' } },
  { selector: 'node.accountingPackage', style: { shape: 'hexagon', 'background-color': '#d97706' } },
  { selector: 'node.item', style: { shape: 'ellipse', 'background-color': '#059669' } },
  {
    selector: 'node.more',
    style: {
      shape: 'ellipse',
      'background-color': '#e2e8f0',
      color: '#475569',
      'border-style': 'dashed',
      'border-color': '#94a3b8',
      'border-width': 2,
    },
  },
  {
    selector: 'edge',
    style: { width: 1, 'line-color': '#cbd5e1', 'curve-style': 'straight', opacity: 0.8 },
  },
  {
    selector: 'node.missing',
    style: { 'border-style': 'dashed', 'border-color': '#dc2626', 'border-width': 3, 'background-opacity': 0.35 },
  },
  {
    selector: 'node.warning',
    style: { 'border-style': 'solid', 'border-color': '#f59e0b', 'border-width': 4 },
  },
  {
    selector: 'node.start',
    style: { 'border-width': 4, 'border-color': '#111827', 'border-style': 'double' },
  },
  {
    selector: 'node.start.warning',
    style: { 'border-color': '#f59e0b' },
  },
  {
    selector: 'node.start.missing',
    style: { 'border-color': '#dc2626', 'border-style': 'double' },
  },
  {
    selector: 'node.default-path',
    style: { 'underlay-color': '#38bdf8', 'underlay-opacity': 0.25, 'underlay-padding': 5 },
  },
  { selector: 'edge.default-path', style: { width: 3, 'line-color': '#38bdf8', 'z-index': 10 } },
  { selector: 'edge.paidWith', style: { 'line-style': 'dashed', width: 2, 'line-color': '#2563eb', 'z-index': 9 } },
  { selector: 'edge.mismatch', style: { 'line-color': '#dc2626', width: 3, 'line-style': 'solid', 'z-index': 11 } },
  { selector: 'edge.defaultsTo', style: { 'line-style': 'dotted', width: 2, 'line-color': '#7c3aed', 'z-index': 9 } },
  { selector: 'edge.defaultsTo.mismatch', style: { 'line-color': '#dc2626' } },
  { selector: '.faded', style: { opacity: 0.12 } },
  { selector: 'edge.highlighted', style: { width: 2, 'line-color': '#475569' } },
  { selector: 'edge.default-path.highlighted', style: { 'line-color': '#38bdf8', width: 3 } },
];

interface LegendEntry {
  key: string;
  icon: string;
  colourClass: string;
}

@Component({
  standalone: false,
  selector: 'wl-gateway-resolution-graph',
  templateUrl: './gateway-resolution-graph.component.html',
  styleUrls: ['./gateway-resolution-graph.component.less'],
})
export class GatewayResolutionGraphComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() itemNum: number | null | undefined;
  @Input() accountingPackageId: number | null | undefined;
  @Input() paymentTypeId: number | null | undefined;
  @Input() ccGatewayId: number | null | undefined;
  @Input() invoiceNum: number | null | undefined;

  @ViewChild('cyContainer', { static: true }) container!: ElementRef<HTMLDivElement>;

  isLoading = false;
  loadError = false;
  hasStart = false;
  hasGraph = false;
  ringGuides: RenderedGuide[] = [];
  hoverLabel: { text: string; count: number | null; missing?: boolean; x: number; y: number } | null = null;
  showDialog = false;
  selectedNode: {
    type: GraphNodeType;
    label: string;
    icon: string;
    data: Record<string, unknown>;
    flags?: GraphNodeFlags;
  } | null = null;
  gatewayExtras: GatewayDetailExtras | null = null;
  gatewayExtrasUnavailable = false;

  readonly legend: LegendEntry[] = [
    { key: 'revenue.gateway-resolution-tree.graph-legend-gateway', icon: NODE_ICON_CLASS.gateway, colourClass: 'legend-gateway' },
    {
      key: 'revenue.gateway-resolution-tree.graph-legend-payment-type',
      icon: NODE_ICON_CLASS.paymentType,
      colourClass: 'legend-payment-type',
    },
    {
      key: 'revenue.gateway-resolution-tree.graph-legend-accounting-package',
      icon: NODE_ICON_CLASS.accountingPackage,
      colourClass: 'legend-accounting-package',
    },
    { key: 'revenue.gateway-resolution-tree.graph-legend-item', icon: NODE_ICON_CLASS.item, colourClass: 'legend-item' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-more', icon: NODE_ICON_CLASS.more, colourClass: 'legend-more' },
  ];
  readonly legendStyles: { key: string; colourClass: string }[] = [
    { key: 'revenue.gateway-resolution-tree.graph-legend-start', colourClass: 'legend-start' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-default-path', colourClass: 'legend-default-path' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-paid-with', colourClass: 'legend-paid-with' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-mismatch', colourClass: 'legend-mismatch' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-missing', colourClass: 'legend-missing' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-warning', colourClass: 'legend-warning' },
    { key: 'revenue.gateway-resolution-tree.graph-legend-default-to', colourClass: 'legend-default-to' },
  ];

  private cy?: Core;
  private graph: GatewayResolutionGraph | null = null;
  private seedKeys: string[] = [];
  private maxItems: number | null = null;
  private _loadSeq = 0;
  private geometry: { ring: RingType; radius: number }[] = [];
  private resizeObserver?: ResizeObserver;
  private pendingFrame: number | null = null;
  private gatewayList?: Promise<Map<number, GatewayDetailExtras>>;

  constructor(
    private _apiService: ApiService,
    private _zone: NgZone,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.loadGraph();
  }

  ngAfterViewInit(): void {
    // The container is static, so the instance is created when the first response arrives.
  }

  ngOnChanges(changes: SimpleChanges): void {
    const keys = ['itemNum', 'accountingPackageId', 'paymentTypeId', 'ccGatewayId', 'invoiceNum'];
    const changed = keys.some((k) => changes[k] && !changes[k].firstChange);
    if (!changed) {
      return;
    }
    this.maxItems = null;
    this.closeDialog();
    this.hoverLabel = null;
    void this.loadGraph();
  }

  ngOnDestroy(): void {
    ++this._loadSeq;
    this.destroyCy();
  }

  /** The only place Cytoscape is instantiated. */
  protected createCy(options: CytoscapeOptions): Core {
    return cytoscape(options);
  }

  async loadGraph(): Promise<void> {
    const seq = ++this._loadSeq;
    this.loadError = false;
    const start = resolveStart(this);
    if (!start) {
      this.hasStart = false;
      this.hasGraph = false;
      this.graph = null;
      this.isLoading = false;
      this.destroyCy();
      this.ringGuides = [];
      return;
    }
    this.hasStart = true;
    this.isLoading = true;
    try {
      const raw = await this._apiService.get(this.financialUrl(buildGraphPath(start, this.maxItems)), undefined, false);
      if (seq !== this._loadSeq) {
        return;
      }
      const g = normalizeGraph(raw);
      if (g === null) {
        throw new Error('Unexpected gateway resolution graph response');
      }
      this.graph = g;
      this.seedKeys = normalizeSeedKeys(raw);
      const defaultPaths = normalizeDefaultPaths(raw);
      this.hasGraph = g.nodes.length > 0;
      this.gatewayList = undefined;
      this._cdr.detectChanges();
      this.render(defaultPaths);
    } catch {
      if (seq === this._loadSeq) {
        this.loadError = true;
        this.hasGraph = false;
        this.graph = null;
        this.destroyCy();
      }
    } finally {
      if (seq === this._loadSeq) {
        this.isLoading = false;
      }
    }
  }

  closeDialog(): void {
    this.showDialog = false;
    this.selectedNode = null;
    this.gatewayExtras = null;
  }

  private financialUrl(path: string): string {
    const base = this._apiService.getServiceBaseUrl('financial');
    return base ? `${base}/${path}` : path;
  }

  private render(defaultPaths: string[][] = []): void {
    this.destroyCy();
    const graph = this.graph;
    if (!graph?.nodes.length) {
      return;
    }
    const cy = this._zone.runOutsideAngular(() =>
      this.createCy({
        container: this.container.nativeElement,
        elements: toElements(graph, this.seedKeys, defaultPaths) as any,
        style: GRAPH_STYLE,
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 3,
        wheelSensitivity: 2.5,
        autoungrabify: true,
        boxSelectionEnabled: false,
        autounselectify: true,
      })
    );
    this.cy = cy;

    this._zone.runOutsideAngular(() => {
      cy.on('layoutstop', () => {
        this.geometry = computeRingGeometry(cy.nodes().map((n) => ({ ring: RING_OF[n.data('type') as GraphNodeType], ...n.position() })));
        this.updateRingGuides();
      });
      cy.on('pan zoom', () => this.scheduleGuideUpdate());
      cy.on('mouseover', 'node', (evt: EventObject) => this.onHover(evt.target as NodeSingular, graph));
      cy.on('mouseout', 'node', () => {
        cy.elements().removeClass('faded highlighted');
        this.hoverLabel = null;
        this._cdr.detectChanges();
      });
      cy.on('tap', 'node', (evt: EventObject) => {
        const id = (evt.target as NodeSingular).id();
        this._zone.run(() => this.onNodeTap(id));
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.cy && !this.cy.destroyed()) {
          this.cy.resize();
          this.updateRingGuides();
        }
      });
      this.resizeObserver.observe(this.container.nativeElement);
    }

    if (typeof document !== 'undefined' && document.fonts?.load) {
      document.fonts.load('16px primeicons').then(
        () => {
          if (this.cy && !this.cy.destroyed()) {
            this.cy.style().update();
          }
        },
        () => {}
      );
    }

    // The layout runs only after the handlers exist so its layoutstop is observed.
    this._zone.runOutsideAngular(() => {
      cy.layout(ringLayoutOptions() as unknown as LayoutOptions).run();
    });
  }

  private onHover(node: NodeSingular, graph: GatewayResolutionGraph): void {
    const cy = this.cy;
    if (!cy) {
      return;
    }
    const ns = neighbourSet(graph.edges, node.id());
    cy.batch(() => {
      cy.elements().forEach((el) => {
        if (ns.nodes.has(el.id()) || ns.edges.has(el.id())) {
          el.addClass('highlighted');
        } else {
          el.addClass('faded');
        }
      });
    });
    const pos = node.renderedPosition();
    const size = Number(node.data('size')) || 0;
    this.hoverLabel = {
      text: String(node.data('label')),
      count: node.data('type') === 'more' ? Number(node.data('count')) : null,
      missing: node.data('isMissing') === true,
      x: pos.x,
      y: pos.y + size / 2 + 6,
    };
    this._cdr.detectChanges();
  }

  private scheduleGuideUpdate(): void {
    if (this.pendingFrame !== null || typeof requestAnimationFrame === 'undefined') {
      return;
    }
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.updateRingGuides();
    });
  }

  private updateRingGuides(): void {
    this.ringGuides = this.cy && !this.cy.destroyed() ? toRenderedGuides(this.geometry, this.cy.pan(), this.cy.zoom()) : [];
    this._cdr.detectChanges();
  }

  private destroyCy(): void {
    if (this.pendingFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.pendingFrame);
    }
    this.pendingFrame = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.cy?.destroy();
    this.cy = undefined;
    this.ringGuides = [];
    this.hoverLabel = null;
  }

  private onNodeTap(key: string): void {
    const node = this.graph?.nodes.find((n) => n.key === key);
    if (!node) {
      return;
    }
    if (node.type === 'more') {
      const next = nextMaxItems(this.maxItems);
      if (next === null) {
        return;
      }
      this.maxItems = next;
      void this.loadGraph();
      return;
    }
    this.selectedNode = {
      type: node.type,
      label: node.label,
      icon: NODE_ICON_CLASS[node.type],
      data: { id: node.id, ...node.data },
      ...(node.flags ? { flags: { ...node.flags } } : {}),
    };
    this.showDialog = true;
    if (node.type === 'gateway' && !node.flags?.isMissing) {
      void this.loadGatewayExtras(node.id);
    }
  }

  private async loadGatewayExtras(id: number): Promise<void> {
    this.gatewayExtras = null;
    this.gatewayExtrasUnavailable = false;
    try {
      this.gatewayList ??= this._apiService.get('CreditCardGateways', undefined, false).then((raw) => {
        if (!Array.isArray(raw)) {
          throw new Error('Unexpected gateway list response');
        }
        const map = new Map<number, GatewayDetailExtras>();
        for (const r of raw) {
          const extras = toGatewayExtras(r);
          if (extras) {
            map.set(extras.ccGatewayId, extras);
          }
        }
        return map;
      });
      const map = await this.gatewayList;
      if (this.selectedNode?.type === 'gateway' && this.selectedNode.data['id'] === id) {
        this.gatewayExtras = map.get(id) ?? null;
        this.gatewayExtrasUnavailable = this.gatewayExtras === null;
      }
    } catch {
      if (this.selectedNode?.type === 'gateway' && this.selectedNode.data['id'] === id) {
        this.gatewayExtrasUnavailable = true;
      }
    }
  }
}
